import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { AccessLevel } from '@dev/ee-api/ee-api-models';
import {
    Alert,
    ArrowDropDownIcon,
    Autocomplete,
    BasicInput,
    ContentLoader,
    palette,
    PrimaryButton,
    TertiaryButton,
    Typography,
    Checkbox,
} from '@dev/ee-components';
import { filter, forEach } from 'lodash-es';
import { Link } from '@material-ui/core';
import styled from 'styled-components';

import { getContactsStore } from '@/store/contacts/actions';
import { getCustomFieldsStore, GetFields } from '@/store/customFields/actions';
import { getAccountStore } from '@/store/account/actions';
import { isReseller } from '@/services/Secuirty';
import { links } from '@/services/dictionaries/links';
import {
    UploadContactField,
    CustomFieldsProps,
    CustomFieldsTable,
    CustomFieldsContentProps,
    SingleEntryProps,
} from '../../ContactsUpload';
import { STEPS, ButtonFooter } from '../..';

const ContactFieldRow = styled.div`
    display: flex;
    justify-content: space-between;
    margin-bottom: 8px;
    & > div {
        display: inline-block;
        padding: 0 15px;
        width: 100%;
    }
    & > div:first-child {
        padding-left: 0;
    }
    & > div:last-child {
        padding-right: 0;
    }
    & > div > div,
    & > div > div > div,
    & .MuiButton-contained {
        width: 100%;
    }
    & .MuiAutocomplete-option[data-focus='true'] {
        background-color: ${palette.indigo[1]};
    }
    & input[readonly] {
        background: transparent;
    }
    & .fieldError .MuiInputBase-root {
        border-color: red !important;
        background-color: #fef5f6;
    }
    & .fieldError .MuiButtonBase-root {
        border-color: red !important;
        background-color: #fef5f6;
    }
    & svg.MuiSvgIcon-root {
        width: 24px;
        height: 24px;
    }
`;

const MultiTypographyContainer = styled.div`
    & > * {
        display: inline-block;
        min-width: 1px;
        padding: 0 3px;
    }
`;

const OverrideCustomFieldsCheckbox = styled.div`
    margin-top: 24px;
`;

const OverrideCustomFieldsLabel = styled.div`
    margin-top: 0.5rem;
`;

let AllFields: any[];
let contactFieldsArrayTemp = [];

export const CustomFields = (props: CustomFieldsProps): JSX.Element => {
    const { contactPattern } = useSelector(getContactsStore);
    const customFieldsStore = useSelector(getCustomFieldsStore);
    const customFields = customFieldsStore.collection;
    const { GeneralInfo } = useSelector(getAccountStore);
    const { t } = useTranslation();

    const {
        recognizedEmails,
        sourceType,
        setCurrentStep,
        errorFields,
        setErrorFields,
        setPattern,
        setContactFieldsArray,
        overrideCustomFields,
        setOverrideCustomFields,
    } = props;

    const getCustomFieldsData = (): void => {
        contactFieldsArrayTemp = [];

        AllFields = customFields?.map((field): string => field.Name);
        const canModifyCustomFields = GeneralInfo?.AccountAccessLevel?.includes(AccessLevel.ModifyCustomFields);
        const basicFields = canModifyCustomFields ? ['newfield', 'firstname', 'lastname'] : ['firstname', 'lastname'];

        AllFields?.unshift(...basicFields);

        const fields = canModifyCustomFields
            ? [
                  {
                      label: t('newfield'),
                      value: 'newfield',
                  },
                  {
                      label: t('ignore'),
                      value: '',
                      borderBottom: true,
                  },
              ]
            : [
                  {
                      label: t('ignore'),
                      value: '',
                      borderBottom: true,
                  },
              ];

        if (contactPattern) {
            forEach(contactPattern.Headers, (header): void => {
                if (header !== 'email') {
                    fields.push({
                        label: header,
                        value: header.toLowerCase(),
                        borderBottom: false,
                    });
                }
            });
            forEach(contactPattern.Example, (item, index): void => {
                if (contactFieldsArrayTemp.length < contactPattern.Example.length) {
                    const element = contactPattern.Pattern[index].toLowerCase();
                    const itemFoundAtIndex = AllFields.findIndex((option): boolean => option.toLowerCase() === element);
                    const field = {
                        index,
                        isNew: false,
                        inputName: contactPattern.Pattern[index],
                        systemName: itemFoundAtIndex > -1 ? element : '',
                        example: item,
                        isError: false,
                    };
                    contactFieldsArrayTemp.push(field);
                    setContactFieldsArray(contactFieldsArrayTemp);
                }
            });
        }
    };

    useEffect(() => {
        getCustomFieldsData();
    }, [customFields, GeneralInfo, contactPattern]);

    if (!contactPattern && !recognizedEmails) {
        return (
            <div>
                <ContentLoader mode="row" />
                <ContentLoader mode="row" count={5} />
            </div>
        );
    }

    return (
        <CustomFieldsContent
            recognizedEmails={recognizedEmails}
            sourceType={sourceType}
            setCurrentStep={setCurrentStep}
            errorFields={errorFields}
            setErrorFields={setErrorFields}
            setPattern={setPattern}
            overrideCustomFields={overrideCustomFields}
            setOverrideCustomFields={setOverrideCustomFields}
            items={contactFieldsArrayTemp}
        />
    );
};

const setContactFieldsPattern = (contactFieldsArrayTemp: UploadContactField[]): any => {
    const pattern = {};
    forEach(contactFieldsArrayTemp, (field): void => {
        if (field.systemName) {
            pattern[field.systemName.toLowerCase()] = field.inputName.toLowerCase();
        }
        if (!field.systemName && field.isNew) {
            pattern[field.inputName.toLowerCase()] = field.inputName.toLowerCase();
        }
    });
    return pattern;
};

const CustomFieldsContent = (props: CustomFieldsContentProps): JSX.Element => {
    const account = useSelector(getAccountStore);
    const { contactPattern } = useSelector(getContactsStore);
    const { t } = useTranslation();
    const { allFields, recognizedFields } = useSelector(getCustomFieldsStore);
    const {
        recognizedEmails,
        sourceType,
        setCurrentStep,
        errorFields,
        setErrorFields,
        setPattern,
        overrideCustomFields,
        setOverrideCustomFields,
        items,
    } = props;

    const contactFieldsDescriptionText: JSX.Element = isReseller(account) ? (
        <span>{t('pleasedefineunknowncontactfields')}</span>
    ) : (
        <span>
            {`${t('pleasedefineunknowncontactfields')} `}
            {
                <Link href={links.createCustomContactFields} rel="noreferrer" target="_blank">
                    {t('learnmore')}
                </Link>
            }
        </span>
    );
    const isUploadSource = sourceType === 'upload' || sourceType === 'mailchimp';
    const numberOfEmails = isUploadSource ? contactPattern?.Contacts : recognizedEmails;
    return (
        <div>
            {(contactPattern || recognizedEmails) && (
                <div>
                    <MultiTypographyContainer>
                        <Typography variant="body1">
                            {isUploadSource ? t('wehavefoundinfile') : t('wehavefound')}
                        </Typography>
                        <Typography variant="h2">{numberOfEmails || '0'}</Typography>
                        <Typography variant="body1"> {t('contacts').toLowerCase()},</Typography>
                        {isUploadSource && (
                            <>
                                <Typography variant="h2"> {recognizedFields || '0'}</Typography>
                                <Typography variant="body1">{t('recognizedfields')},</Typography>
                                <Typography variant="h2">
                                    {allFields - recognizedFields > 0 ? allFields - recognizedFields : '0'}
                                </Typography>
                                <Typography variant="body1">{t('unknowncontactfields')} </Typography>
                            </>
                        )}
                    </MultiTypographyContainer>
                    {contactPattern && (
                        <div style={{ margin: '32px 0' }}>
                            <Alert severity="info" body={contactFieldsDescriptionText} />
                        </div>
                    )}
                    {contactPattern && (
                        <CustomFieldsTable errorFields={errorFields} setErrorFields={setErrorFields} items={items} />
                    )}
                    {isUploadSource && (
                        <OverrideCustomFieldsCheckbox>
                            <Checkbox
                                id="override-custom-fields-checkbox"
                                name="overridecustomfields"
                                value={overrideCustomFields}
                                disabled={false}
                                required={false}
                                label={
                                    <>
                                        <OverrideCustomFieldsLabel>
                                            {t('updateexistingcontacts')}
                                        </OverrideCustomFieldsLabel>
                                        <Typography variant="body2">{t('descoverridecustomfields')}</Typography>
                                    </>
                                }
                                onChange={(e, checked): void => setOverrideCustomFields(checked)}
                                indeterminate={false}
                                labelPlacement="end"
                                align="flex-start"
                            />
                        </OverrideCustomFieldsCheckbox>
                    )}
                </div>
            )}
            {!contactPattern && !recognizedEmails && (
                <div>
                    <ContentLoader mode="row" />
                    <ContentLoader mode="row" count={5} />
                </div>
            )}
            <ButtonFooter>
                <TertiaryButton
                    id="back-button"
                    text={t('back')}
                    onClick={(): void => setCurrentStep(STEPS.CONTACT_ADDING_METHODS)}
                />
                <PrimaryButton
                    id="continue-button"
                    disabled={(!contactPattern || errorFields) && !recognizedEmails}
                    text={t('continue')}
                    onClick={(): void => {
                        setPattern(setContactFieldsPattern(contactFieldsArrayTemp));
                        setCurrentStep(STEPS.CONTACT_ASSIGNING);
                    }}
                />
            </ButtonFooter>
        </div>
    );
};

const setNewFieldNameAndCheckErrors = (
    field,
    newSystemName: string,
    canModifyCustomFields,
    contactFieldsArrayTemp: UploadContactField[]
): void => {
    let newFieldValue = newSystemName;

    if (!canModifyCustomFields) {
        const isInSelectedOptions = AllFields?.includes(newFieldValue);
        newFieldValue = isInSelectedOptions ? newFieldValue : '';
    }

    if (field.isError) {
        const sameNameFields = filter(
            contactFieldsArrayTemp,
            (entry: UploadContactField): boolean => entry.systemName === field.systemName
        );
        if (sameNameFields.length <= 2) {
            //there is only one duplicate
            sameNameFields.forEach((item): void => {
                contactFieldsArrayTemp[item.index].isError = false;
            });
        } else {
            field.isError = false;
        }
    }
    if (newFieldValue !== '') {
        contactFieldsArrayTemp.forEach((entry): void => {
            if (entry.systemName === newFieldValue && entry.index !== field.index) {
                field.isError = true;
                entry.isError = true;
            }
        });
    }

    if (newFieldValue === 'newfield') {
        field.isNew = true;
    } else {
        field.systemName = newFieldValue;
    }

    ///this.forceUpdate(); ??????
};

const SingleEntry = (props: SingleEntryProps): JSX.Element => {
    const { contactPattern } = useSelector(getContactsStore);
    const { GeneralInfo } = useSelector(getAccountStore);
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { errorFields, setErrorFields, item: field } = props;

    const element = field.inputName.toLowerCase();
    const itemExists = AllFields.findIndex((option): boolean => option.toLowerCase() === element);
    field.systemName = field.systemName !== '' ? field.systemName : itemExists > -1 ? element : '';
    const canModifyCustomFields = GeneralInfo.AccountAccessLevel.includes(AccessLevel.ModifyCustomFields);
    if (element === 'email') {
        field.systemName = 'email';
    }
    const popoverClasses = field.isError ? 'popoverContainer fieldError' : 'popoverContainer';
    const checkFieldsValidation = (): void => {
        const fieldsWithError = filter(contactFieldsArrayTemp, (entry): boolean => entry.isError);
        if (!!fieldsWithError.length !== errorFields) {
            setErrorFields(fieldsWithError.length > 0 ? true : false);
        }
    };

    return (
        <ContactFieldRow key={field.index}>
            <div className={popoverClasses}>
                {element !== 'email' && (
                    <Autocomplete
                        id={`autocomplete-${field.index}`}
                        value={field.systemName}
                        options={AllFields}
                        openOnFocus={true}
                        freeSolo={canModifyCustomFields}
                        disableClearable={true}
                        placeholder={t('selectorcreatefield')}
                        popupIcon={<ArrowDropDownIcon />}
                        forcePopupIcon={true}
                        onBlur={(e): void => {
                            setNewFieldNameAndCheckErrors(
                                field,
                                (e.target as HTMLInputElement).value,
                                canModifyCustomFields,
                                contactFieldsArrayTemp
                            );
                            const recognizedFields = filter(
                                contactFieldsArrayTemp,
                                (entry): string => entry.systemName
                            );
                            dispatch(GetFields(contactPattern.Example.length, recognizedFields.length));
                            checkFieldsValidation();
                        }}
                        onChange={(e, obj: string): void => {
                            setNewFieldNameAndCheckErrors(field, obj, canModifyCustomFields, contactFieldsArrayTemp);
                            const recognizedFields = filter(
                                contactFieldsArrayTemp,
                                (entry): string => entry.systemName
                            );
                            dispatch(GetFields(contactPattern.Example.length, recognizedFields.length));
                            checkFieldsValidation();
                        }}
                    />
                )}
                {element === 'email' && !field.isNew && <BasicInput id="email-input" value="email" readOnly={true} />}
            </div>
            {(field.inputName !== 'email' && element === 'email') ||
                (field.isNew && (
                    <div>
                        <BasicInput id={`${field.inputName}-input`} value={field.inputName} readOnly={true} />
                    </div>
                ))}
            <div>
                <BasicInput id={`${field.inputName}-example-input`} value={field.example} readOnly={true} />
            </div>
        </ContactFieldRow>
    );
};

const CustomFieldsTable = (props: CustomFieldsTable): JSX.Element => {
    const { errorFields, setErrorFields, items } = props;
    return (
        <div>
            {items?.map(
                (item, index): JSX.Element => (
                    <SingleEntry key={index} item={item} errorFields={errorFields} setErrorFields={setErrorFields} />
                )
            )}
        </div>
    );
};
